import React from 'react';
import { useTranslation } from 'react-i18next';

/** Default correspondence pace when switching from live → corr without picking. */
export const CORRESPONDENCE_DAYS_PER_MOVE = 3;

export const CORRESPONDENCE_PACE_OPTIONS = [1, 3, 7];

/**
 * Live clock vs correspondence with 1 / 3 / 7 days-per-move presets.
 * @param {{
 *   value: number,
 *   onChange: (days: number) => void,
 *   className?: string,
 *   layout?: 'select' | 'segmented',
 * }} props
 *   value: 0 = live clock; >0 = correspondence days per move
 */
export default function TimeModeSelect({
  value,
  onChange,
  className = '',
  layout = 'select',
}) {
  const { t } = useTranslation();
  const days = Number(value) > 0 ? Number(value) : 0;
  const selectValue = days > 0
    ? (CORRESPONDENCE_PACE_OPTIONS.includes(days) ? days : days)
    : 0;

  if (layout === 'segmented') {
    const options = [
      { value: 0, label: t('playing.liveClock') },
      ...CORRESPONDENCE_PACE_OPTIONS.map((n) => ({
        value: n,
        label: t('playing.daysPerMoveShort', {
          defaultValue: '{{n}}d / move',
          n,
        }),
        title: t('playing.correspondenceMode', {
          defaultValue: 'Correspondence ({{n}} days per move)',
          n,
        }),
      })),
    ];

    return (
      <div className={`playing-time-mode ${className}`.trim()}>
        <p className="playing-hub-field-label">
          {t('playing.correspondence')}
        </p>
        <div
          className="playing-time-mode__seg"
          role="group"
          aria-label={t('playing.correspondence')}
        >
          {options.map((opt) => {
            const selected = opt.value === 0
              ? days === 0
              : Number(opt.value) === Number(days);
            return (
              <button
                key={opt.value}
                type="button"
                className={`playing-time-mode__btn${selected ? ' playing-time-mode__btn--active' : ''}`}
                aria-pressed={selected}
                title={opt.title || opt.label}
                onClick={() => onChange(opt.value)}
                data-testid={opt.value === 0 ? 'playing-time-mode-live' : `playing-time-mode-d${opt.value}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        {days > 0 && !CORRESPONDENCE_PACE_OPTIONS.includes(days) && (
          <p className="mt-1.5 text-xs text-stone-500">
            {t('playing.correspondenceMode', {
              defaultValue: 'Correspondence ({{n}} days per move)',
              n: days,
            })}
          </p>
        )}
      </div>
    );
  }

  return (
    <label className={`block text-sm font-medium text-slate-700 ${className}`}>
      {t('playing.correspondence')}
      <select
        className="select mt-1"
        value={selectValue}
        onChange={(e) => {
          const next = Number(e.target.value);
          onChange(next > 0 ? next : 0);
        }}
        data-testid="playing-time-mode"
      >
        <option value={0}>{t('playing.liveClock')}</option>
        {CORRESPONDENCE_PACE_OPTIONS.map((n) => (
          <option key={n} value={n}>
            {t('playing.correspondenceMode', {
              defaultValue: 'Correspondence ({{n}} days per move)',
              n,
            })}
          </option>
        ))}
      </select>
    </label>
  );
}
